import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import * as api from '../api.ts';
import { Button, Finding, Status } from '../ui.tsx';

/**
 * Who can raise a real alert at this building (migration 0134, the product
 * owner's ruling of 2026-09-29, docs/security.md §1.1). Two radios with no
 * default — the one decision on this step that must never be made by accident.
 * Rendered by `ActivationRuleSetting` below, which both the building page's
 * Wardens section and the wizard's wardens step use, so both offer the
 * identical wording.
 *
 * Wording rules (sanity board): a staff-only alert reaches EVERYONE at the
 * building — students, visitors, patients — so the copy says "the people at
 * this building", never "staff"; and it says "administrator" wherever it means
 * one, never "person".
 *
 * `two_person` is disabled while the organisation has fewer than two current
 * administrators: a two-administrator building in a one-administrator org could
 * never send a real alert, and the admin must learn that here, not during the
 * emergency. The server refuses the same choice (409 needs_second_admin).
 */

export const ACTIVATION_RULE_OPTIONS: {
  value: api.ActivationRule;
  label: string;
  hint: string;
}[] = [
  {
    value: 'one_person',
    label: 'One administrator can send a real alert to the people at this building',
    hint: 'One administrator can alert everyone at the building straight away. An alert that reaches further still needs a second administrator.',
  },
  {
    value: 'two_person',
    label: 'Two administrators must agree before any real alert is sent',
    hint: 'Every real alert here waits until a second administrator authorises it, even one only for the people at this building. Drills still need only one.',
  },
];

export const ONLY_ADMIN_SENTENCE =
  "You're the only administrator — a second administrator must join before this building can require two.";

/** Said when a two-administrator building has no linked teams (re-review HIGH-1). */
export const LINK_TEAMS_SENTENCE =
  "Link this building's teams so only its people need two administrators — until then, real alerts from your other buildings will also need a second administrator.";

export function ActivationRuleChoice({
  value,
  onChange,
  name = 'activation-rule',
  adminCount,
  readOnly = false,
  linkedTeams,
}: {
  value: api.ActivationRule | null;
  onChange: (v: api.ActivationRule) => void;
  name?: string;
  /** Current administrators and owners; `undefined` while unknown. */
  adminCount?: number;
  /** A non-admin sees the rule but cannot change it. */
  readOnly?: boolean;
  /**
   * Whether teams are linked to this building. A two-administrator building
   * with none protects the whole roster, which reaches every other building's
   * real alerts too — said in words. `undefined` while unknown.
   */
  linkedTeams?: boolean;
}) {
  const soleAdmin = adminCount !== undefined && adminCount < 2;
  return (
    <fieldset className="mt-4 max-w-[640px]" aria-required="true">
      <legend className="mb-2 text-[14px] font-medium">Who can raise a real alert here?</legend>
      <div className="space-y-2">
        {ACTIVATION_RULE_OPTIONS.map((o) => {
          // Never disable the option that is already the building's rule —
          // it must stay visibly selected even when it can no longer be met.
          const disabled =
            readOnly || (o.value === 'two_person' && soleAdmin && value !== 'two_person');
          return (
            <label
              key={o.value}
              className={`flex items-start gap-2.5 text-[14px] ${disabled && value !== o.value ? 'opacity-60' : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={value === o.value}
                disabled={disabled}
                required
                onChange={() => {
                  if (!disabled) onChange(o.value);
                }}
                className="mt-1"
              />
              <span>
                <span className="block">{o.label}</span>
                <span className="mt-0.5 block text-[13px] leading-[18px] text-[var(--ink-2)]">
                  {o.hint}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {value === 'two_person' && linkedTeams === false ? (
        <p className="mt-2 text-[13px] leading-[18px] text-[var(--attention)]">
          {LINK_TEAMS_SENTENCE}
        </p>
      ) : null}
      {soleAdmin && !readOnly ? (
        <p className="mt-2 text-[13px] leading-[18px] text-[var(--ink-2)]">{ONLY_ADMIN_SENTENCE}</p>
      ) : null}
    </fieldset>
  );
}

/**
 * Who can raise a real alert at this building (migration 0134), as a saved
 * setting. It lives with the building's wardens — on the building page's
 * Wardens section and the Add-a-building wizard's wardens step (product owner,
 * 2026-09-30: "it should be on the warden page", not in the building's
 * details) — and both render THIS component, so the rules below are one
 * implementation, never two. Tightening saves on the click; loosening (two → one administrator) is
 * asked for in words, recorded as a request, and changes only when a DIFFERENT
 * administrator confirms it within 24 hours (security board H2) — this control
 * shows that waiting state and offers Confirm to the other administrators.
 * Read-only for anyone who is not an administrator.
 *
 * A building created without a choice is stored as one administrator with
 * `activation_rule_chosen_at` NULL, and readiness asks for it. Until then
 * neither radio is selected, and picking "one administrator" is a real save
 * (the server stamps the choice) rather than a no-op on the default.
 */
export function ActivationRuleSetting({
  orgId,
  siteId,
  site,
  onSaved,
}: {
  orgId: string;
  siteId: string;
  site: api.Site | undefined;
  onSaved: () => void;
}) {
  const me = useQuery({ queryKey: ['me'], queryFn: api.getMe });
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  const myRole = me.data?.memberships.find((m) => m.org_id === orgId)?.role;
  const isAdmin = myRole ? api.isAdminRole(myRole) : false;
  const current: api.ActivationRule = site?.activation_rule ?? 'one_person';
  const pendingBy = site?.activation_rule_loosen_requested_by ?? null;
  const pendingMine = pendingBy !== null && pendingBy === me.data?.userId;
  const pendingOther = pendingBy !== null && !pendingMine;
  // Strictly null: an older API that omits the field reads as chosen.
  const unchosen = site?.activation_rule_chosen_at === null && pendingBy === null;

  // The value the admin just chose, held until the save lands so the radio
  // never snaps back to the old rule for a moment (arch board L4).
  const [chosen, setChosen] = useState<api.ActivationRule | null>(null);
  const [confirmLoosen, setConfirmLoosen] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(null), 4000);
    return () => clearTimeout(t);
  }, [saved]);

  const change = useMutation({
    mutationFn: (activationRule: api.ActivationRule) =>
      api.updateSite(orgId, siteId, { activationRule }),
    onSuccess: (res, rule) => {
      setChosen(null);
      setConfirmLoosen(false);
      setError(null);
      setSaved(
        res?.pendingLoosen
          ? 'Asked. Waiting for a second administrator to confirm.'
          : rule === 'two_person'
            ? 'Saved. Two administrators must now agree before any real alert.'
            : 'Saved.',
      );
      onSaved();
    },
    onError: (e: Error) => {
      setChosen(null);
      setError(
        e instanceof api.ApiError && e.detail.code === 'stale_session'
          ? 'Sign in again to change this.'
          : e.message,
      );
    },
  });

  const pick = (v: api.ActivationRule) => {
    setError(null);
    if (v === current && !pendingMine && !unchosen) return;
    if (v === 'one_person' && current === 'two_person' && !pendingOther) {
      // Loosening is asked for in words first (sanity board MEDIUM 7).
      setConfirmLoosen(true);
      return;
    }
    setChosen(v);
    change.mutate(v);
  };

  return (
    <div>
      <ActivationRuleChoice
        name={`activation-rule-${siteId}`}
        value={chosen ?? (unchosen ? null : current)}
        onChange={pick}
        adminCount={roster.data?.counts?.admins}
        readOnly={!isAdmin || change.isPending}
        linkedTeams={site?.has_linked_teams}
      />
      {confirmLoosen ? (
        <div className="mt-3 flex max-w-[640px] flex-wrap items-center gap-3">
          <span className="text-[14px] text-[var(--ink-2)]">
            Let one administrator send a real alert here? This only changes if a second
            administrator confirms it within 24 hours. Until then, two are still needed.
          </span>
          <Button
            label="Ask a second administrator to confirm"
            onClick={() => {
              setConfirmLoosen(false);
              setChosen('one_person');
              change.mutate('one_person');
            }}
          >
            Ask to change
          </Button>
          <Button variant="quiet" onClick={() => setConfirmLoosen(false)}>
            Keep two administrators
          </Button>
        </div>
      ) : null}
      {pendingMine ? (
        <div className="mt-3 flex max-w-[640px] flex-wrap items-center gap-3">
          <Finding tone="attention">
            Waiting for a second administrator to confirm. Until one does, every real alert here
            still needs two administrators.
          </Finding>
          <Button variant="quiet" onClick={() => change.mutate('two_person')}>
            Withdraw the request
          </Button>
        </div>
      ) : null}
      {pendingOther && isAdmin ? (
        <div className="mt-3 flex max-w-[640px] flex-wrap items-center gap-3">
          <Finding tone="attention">
            Another administrator has asked to let one administrator send a real alert to the people
            at this building. It changes only if you confirm.
          </Finding>
          <Button
            label="Confirm: one administrator can send a real alert here"
            onClick={() => change.mutate('one_person')}
          >
            Confirm
          </Button>
          <Button variant="quiet" onClick={() => change.mutate('two_person')}>
            Keep two administrators
          </Button>
        </div>
      ) : null}
      {change.isPending ? (
        <p className="mt-2 text-[13px] text-[var(--ink-2)]">Saving…</p>
      ) : saved ? (
        <div className="mt-2">
          <Status tone="safe">{saved}</Status>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2">
          <Finding tone="alert">{error}</Finding>
        </div>
      ) : null}
    </div>
  );
}
